import { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);

  useEffect(() => {
    // Detectar se é dispositivo móvel
    const checkMobile = () => {
      const ua = navigator.userAgent;
      const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      setIsMobile(mobile);

      // iOS (inclui iPadOS que reporta como Mac com touch)
      const iOSUA = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1);
      setIsIOS(iOSUA);
      // Safari em iOS (exclui Chrome/Firefox/Edge iOS)
      const safariIOS = iOSUA && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
      setIsSafari(ua.includes('Safari') ? safariIOS : false);
    };

    // Detectar se o PWA já foi instalado
    const checkIfInstalled = () => {
      // Verificar se está em modo standalone (instalado)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }
      
      // Verificar se está em modo fullscreen (instalado)
      if (window.matchMedia('(display-mode: fullscreen)').matches) {
        setIsInstalled(true);
        return true;
      }
      
      // Verificar se está em modo minimal-ui (instalado)
      if (window.matchMedia('(display-mode: minimal-ui)').matches) {
        setIsInstalled(true);
        return true;
      }
      
      return false;
    };

    checkMobile();
    checkIfInstalled();

    // Capturar o evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    // Detectar quando o app é instalado
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    // Detectar mudanças no modo de exibição
    const handleDisplayModeChange = (e: MediaQueryListEvent) => {
      if (e.matches) {
        setIsInstalled(true);
        setIsInstallable(false);
      }
    };

    // Adicionar event listeners
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    // Monitorar mudanças no modo de exibição
    const mediaQueries = [
      window.matchMedia('(display-mode: standalone)'),
      window.matchMedia('(display-mode: fullscreen)'),
      window.matchMedia('(display-mode: minimal-ui)')
    ];
    
    mediaQueries.forEach(mediaQuery => {
      mediaQuery.addEventListener('change', handleDisplayModeChange);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      
      mediaQueries.forEach(mediaQuery => {
        mediaQuery.removeEventListener('change', handleDisplayModeChange);
      });
    };
  }, []);

  const installPWA = async (): Promise<boolean> => {
    if (!deferredPrompt) {
      return false;
    }

    try {
      // Mostrar o prompt nativo de instalação
      await deferredPrompt.prompt();
      
      // Aguardar a escolha do usuário
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
        setDeferredPrompt(null);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
      return false;
    }
  };

  const canInstall = isInstallable && isMobile && !isInstalled;
  const canShowIOSInstall = isMobile && isIOS && !isInstalled;

  return {
    canInstall,
    isInstalled,
    isMobile,
    installPWA,
    deferredPrompt,
    isIOS,
    isSafari,
    canShowIOSInstall
  };
};